import type { DrillType } from './types';

// ─── Drill tier levels ────────────────────────────────────────────
export type DrillTierLevel = 'bronze' | 'silver' | 'gold' | 'elite';

export interface DrillTier {
  level: DrillTierLevel;
  label: string;
  color: string;
  minScore: number;
}

// What "score" means for each drill
export type DrillScoreType =
  | 'high_break'        // lineup, break_building: highest break reached before miss
  | 'consecutive_pots'  // long_pot, baulk_colors, colors_clearance: consecutive pots without a miss
  | 'safety_pct';       // safety_exchange: % good safeties

export interface DrillTierConfig {
  drillId: DrillType;
  scoreType: DrillScoreType;
  scoreLabel: string;
  maxScore: number;
  tiers: DrillTier[];
}

const TIER_COLOURS = {
  bronze: '#cd7f32',
  silver: '#adb5bd',
  gold:   '#c9a84c',
  elite:  '#8e44ad',
};

export const DRILL_TIER_CONFIGS: Record<DrillType, DrillTierConfig> = {

  // Line-up: 15 reds each followed by a colour.
  // Miss = drill over. Score = break reached.
  lineup: {
    drillId: 'lineup',
    scoreType: 'high_break',
    scoreLabel: 'Break reached',
    maxScore: 147,
    tiers: [
      { level: 'bronze', label: 'Bronze', color: TIER_COLOURS.bronze, minScore: 20  },
      { level: 'silver', label: 'Silver', color: TIER_COLOURS.silver, minScore: 50  },
      { level: 'gold',   label: 'Gold',   color: TIER_COLOURS.gold,   minScore: 80  },
      { level: 'elite',  label: 'Elite',  color: TIER_COLOURS.elite,  minScore: 120 },
    ],
  },

  // Long pot: pot reds from baulk end, consecutive pots without a miss.
  long_pot: {
    drillId: 'long_pot',
    scoreType: 'consecutive_pots',
    scoreLabel: 'Consecutive pots',
    maxScore: 15,
    tiers: [
      { level: 'bronze', label: 'Bronze', color: TIER_COLOURS.bronze, minScore: 1  },
      { level: 'silver', label: 'Silver', color: TIER_COLOURS.silver, minScore: 5  },
      { level: 'gold',   label: 'Gold',   color: TIER_COLOURS.gold,   minScore: 10 },
      { level: 'elite',  label: 'Elite',  color: TIER_COLOURS.elite,  minScore: 14 },
    ],
  },

  // Colours clearance: pot 6 colours in order from spots, cycling if all cleared.
  // Score = consecutive pots before first miss. One full cycle = 6 pots.
  //
  //   Bronze =  3 pots (half a cycle)
  //   Silver =  6 pots (1 full cycle)
  //   Gold   = 12 pots (2 full cycles)
  //   Elite  = 18 pots (3 full cycles — exceptional)
  colors_clearance: {
    drillId: 'colors_clearance',
    scoreType: 'consecutive_pots',
    scoreLabel: 'Consecutive pots',
    maxScore: 24,
    tiers: [
      { level: 'bronze', label: 'Bronze', color: TIER_COLOURS.bronze, minScore: 3  },
      { level: 'silver', label: 'Silver', color: TIER_COLOURS.silver, minScore: 6  },
      { level: 'gold',   label: 'Gold',   color: TIER_COLOURS.gold,   minScore: 12 },
      { level: 'elite',  label: 'Elite',  color: TIER_COLOURS.elite,  minScore: 18 },
    ],
  },

  // Baulk colours: yellow, green, brown from spots, cycling repeatedly.
  // Score = consecutive pots without a miss. One full cycle = 3 pots.
  //
  //   Bronze =  6 pots (2 full cycles)
  //   Silver = 12 pots (4 full cycles)
  //   Gold   = 24 pots (8 full cycles)
  //   Elite  = 40 pots (13+ cycles — exceptional consistency)
  baulk_colors: {
    drillId: 'baulk_colors',
    scoreType: 'consecutive_pots',
    scoreLabel: 'Consecutive pots',
    maxScore: 50,
    tiers: [
      { level: 'bronze', label: 'Bronze', color: TIER_COLOURS.bronze, minScore: 6  },
      { level: 'silver', label: 'Silver', color: TIER_COLOURS.silver, minScore: 12 },
      { level: 'gold',   label: 'Gold',   color: TIER_COLOURS.gold,   minScore: 24 },
      { level: 'elite',  label: 'Elite',  color: TIER_COLOURS.elite,  minScore: 40 },
    ],
  },

  // Safety exchange: safety battle simulation.
  // Score = % of safeties rated 'good'.
  safety_exchange: {
    drillId: 'safety_exchange',
    scoreType: 'safety_pct',
    scoreLabel: 'Good safety %',
    maxScore: 100,
    tiers: [
      { level: 'bronze', label: 'Bronze', color: TIER_COLOURS.bronze, minScore: 40 },
      { level: 'silver', label: 'Silver', color: TIER_COLOURS.silver, minScore: 60 },
      { level: 'gold',   label: 'Gold',   color: TIER_COLOURS.gold,   minScore: 75 },
      { level: 'elite',  label: 'Elite',  color: TIER_COLOURS.elite,  minScore: 90 },
    ],
  },

  // Break building: open practice, highest break in session.
  break_building: {
    drillId: 'break_building',
    scoreType: 'high_break',
    scoreLabel: 'Break reached',
    maxScore: 147,
    tiers: [
      { level: 'bronze', label: 'Bronze', color: TIER_COLOURS.bronze, minScore: 20  },
      { level: 'silver', label: 'Silver', color: TIER_COLOURS.silver, minScore: 50  },
      { level: 'gold',   label: 'Gold',   color: TIER_COLOURS.gold,   minScore: 100 },
      { level: 'elite',  label: 'Elite',  color: TIER_COLOURS.elite,  minScore: 130 },
    ],
  },
};

/**
 * Get the current tier for a given drill score.
 * Returns null if score is below the lowest threshold.
 */
export function getDrillTier(drillId: DrillType, score: number): DrillTier | null {
  const config = DRILL_TIER_CONFIGS[drillId];
  if (!config) return null;
  let result: DrillTier | null = null;
  for (const tier of config.tiers) {
    if (score >= tier.minScore) result = tier;
  }
  return result;
}

/**
 * Get the next tier above the current score.
 * Returns null if already at max tier.
 */
export function getNextDrillTier(drillId: DrillType, score: number): DrillTier | null {
  const config = DRILL_TIER_CONFIGS[drillId];
  if (!config) return null;
  for (const tier of config.tiers) {
    if (score < tier.minScore) return tier;
  }
  return null;
}

/**
 * Get progress (0–100) toward the next drill tier.
 */
export function getDrillTierProgress(drillId: DrillType, score: number): number {
  const next = getNextDrillTier(drillId, score);
  if (!next) return 100;
  return Math.min(Math.round((score / next.minScore) * 100), 99);
}